import { useContext, useEffect, useState } from "react";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import {
  useImpuestosApi,
  ImpuestoItem,
  ImpuestoDto,
} from "../../../services/api/impuestosApi";
import ImpuestoDataTable from "../../../components/module/admin/GenericDataTable/ImpuestoDataTable";
import ImpuestoModal from "../../../components/module/admin/ImpuestoModal/ImpuestoModal";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";

const Impuestos = () => {
  const [items, setItems] = useState<ImpuestoItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ImpuestoItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useImpuestosApi();
  const { darkMode } = useContext(DarkModeContext);
  
  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 90 },
    { field: "Nombre", headerName: "Nombre", flex: 1, minWidth: 200 },
    {
      field: "Valor",
      headerName: "Valor (%)",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => `${params.value}%`,
    },
    {
      field: "Activo",
      headerName: "Estado",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Switch
          checked={params.row.Activo}
          onChange={(e) => handleStatusChange(params.row.Id, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    const data = await getAll();
    if (Array.isArray(data)) {
      setItems(data);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: ImpuestoItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción eliminará el impuesto.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        await remove(id);
        await loadItems();
        toast.success("El impuesto ha sido eliminado.");
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el registro.");
      }
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: `¿Desea ${active ? "activar" : "desactivar"} este impuesto?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${active ? "activar" : "desactivar"}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const item = items.find((item) => item.Id === id);
        if (item) {
          const updateData: ImpuestoDto = {
            Nombre: item.Nombre,
            Valor: item.Valor,
            Activo: active,
          };
          await update(id, updateData);
          await loadItems();
          toast.success("El estado del impuesto ha sido actualizado.");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("No se pudo actualizar el estado.");
      }
    }
  };

  const handleModalSubmit = async (data: ImpuestoDto) => {
    try {
      if (editingItem) {
        await update(editingItem.Id, data);
      } else {
        await create(data);
      }
      setIsModalOpen(false);
      await loadItems();
      toast.success("Registro guardado correctamente");
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error("No se pudo guardar el registro.");
    }
  };

  return (
    <PageLayout icon={AccountBalanceIcon} title="Impuestos">
        <div style={{ padding: "24px" }}>
          <ImpuestoDataTable
            items={items}
            columns={columns}          
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAdd={handleAdd}
            isLoading={isLoading}
          />
          <ImpuestoModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleModalSubmit}
            title={editingItem ? "Editar Impuesto" : "Nuevo Impuesto"}
            initialData={
              editingItem
                ? {
                    Nombre: editingItem.Nombre,
                    Valor: editingItem.Valor,
                    Activo: editingItem.Activo,
                  }
                : undefined
            }
            isLoading={isLoading}
            isEdit={!!editingItem}
          />
        </div>
      </PageLayout>
  );
};

export default Impuestos;